import {AnchoredOverlay, Button, IconButton, type ButtonBaseProps} from '@primer/react'
import type React from 'react'
import {useState} from 'react'
import {CodeIcon, TriangleDownIcon} from '@primer/octicons-react'
import {usePrefetchCodespacesQuery} from '@github-ui/codespaces-tab/components/use-codespaces-query'
import {usePrefetchCodespacesData} from '@github-ui/codespaces-tab/components/use-codespaces-data'
import {isReactCodespacesTabEnabled} from '@github-ui/codespaces-tab/components/flags'
import {preloadCodespacesTabReact} from '@github-ui/codespaces-tab/components/CodespacesTab'

export interface CodeMenuButtonProps {
  children: React.ReactNode
  className?: string
  size?: 'small' | 'large' | 'medium'
  onOpenChange?: (open: boolean) => void
  codespacesPath?: string
  shouldPrefetchCodespaces?: boolean
  iconOnly?: boolean
  variant?: ButtonBaseProps['variant']
}

export const CodeMenuButton = ({
  children,
  className,
  size,
  onOpenChange,
  codespacesPath,
  variant,
  shouldPrefetchCodespaces = false,
  iconOnly = false,
}: CodeMenuButtonProps) => {
  const [showPopover, setShowPopover] = useState(false)
  const prefetchHtml = usePrefetchCodespacesQuery(codespacesPath)
  const prefetchJson = usePrefetchCodespacesData(codespacesPath)

  const prefetch = () => {
    if (!shouldPrefetchCodespaces) return

    if (isReactCodespacesTabEnabled()) {
      preloadCodespacesTabReact()
      prefetchJson()
    } else {
      prefetchHtml()
    }
  }

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      open={showPopover}
      onOpen={() => {
        prefetch()
        setShowPopover(true)
        onOpenChange?.(true)
      }}
      onClose={() => {
        setShowPopover(false)
        onOpenChange?.(false)
      }}
      renderAnchor={buttonProps => {
        if (iconOnly) {
          return (
            <IconButton
              {...buttonProps}
              className={className}
              variant={variant}
              icon={CodeIcon}
              aria-label="Code"
              size={size ?? 'medium'}
              onMouseEnter={prefetch}
              onFocus={prefetch}
            />
          )
        }
        return (
          <Button
            {...buttonProps}
            className={className}
            variant={variant}
            leadingVisual={() => <CodeIcon className="hide-sm" />}
            trailingVisual={() => <TriangleDownIcon />}
            size={size ? size : 'medium'}
            onMouseEnter={prefetch}
            onFocus={prefetch}
          >
            Code
          </Button>
        )
      }}
    >
      {children}
    </AnchoredOverlay>
  )
}
